import { useCallback } from 'react';
import { useCurrencyContext } from './currency.provider';
import type { CurrencyFormatterArgs } from './types';
import { getCharmPriceForQuantity } from './utils';
import { minorToMajorUnits } from './minorUnits';

export type FormatCurrencyHandler = (args: CurrencyFormatterArgs & { hideZeroDecimals?: boolean }) => string;

export const useCurrencyFormatter = () => {
  const currency = useCurrencyContext();

  const formatCurrency = useCallback<FormatCurrencyHandler>(
    args => {
      const {
        priceFloatingPointDecimalString,
        priceCurrencyCode,
        formatForm,
        formatPrecision,
        shouldRoundPriceUp,
        shouldCalculateCharmPrice,
        canShowFreeLabel,
        hideZeroDecimals = false,
      } = args;

      // the stringified price is expressed in Major Units
      const floatingPointDecimalString = priceFloatingPointDecimalString ?? '0';

      if (canShowFreeLabel && ['0', '0.00'].includes(floatingPointDecimalString)) {
        // TODO:  i18n
        return 'FREE';
      }

      const priceInMajorUnits = +floatingPointDecimalString;
      const selectedPrice = shouldRoundPriceUp
        ? Math.ceil(priceInMajorUnits)
        : shouldCalculateCharmPrice
          ? getCharmPriceForQuantity(floatingPointDecimalString, 1, priceInMajorUnits)
          : priceInMajorUnits;

      const isWholeNumber = Number.isInteger(selectedPrice);
      const precision = typeof formatPrecision === 'number' ? formatPrecision : hideZeroDecimals && isWholeNumber ? 0 : 2;

      const formattedPrice = currency.formatCurrency(selectedPrice, {
        currency: priceCurrencyCode,
        form: formatForm,
        precision,
      });

      return formattedPrice;
    },
    [currency],
  );

  const formatMinorUnitsCurrency = useCallback(
    (minorUnits: number, currencyCode: string): string => {
      const majorUnits = minorToMajorUnits(minorUnits, currencyCode);
      return formatCurrency({ priceFloatingPointDecimalString: majorUnits.toString(), priceCurrencyCode: currencyCode, formatForm: 'auto' });
    },
    [formatCurrency],
  );

  const getCurrencySymbol = useCallback((currencyCode: string): string => currency.getCurrencySymbol(currencyCode).symbol, [currency]);

  // The short symbol (e.g. "$", not "US$"), plus whether it sits before or after the amount in
  // this locale -- for callers that render the glyph and the number as separate pieces (with
  // their own spacing), unlike `formatCurrency`, which concatenates the two directly and so
  // needs the locale's adjacent whitespace kept on the symbol.
  const getShortCurrencySymbol = useCallback(
    (currencyCode: string) => {
      const { symbol, prefixed } = currency.getShortCurrencySymbol(currencyCode);
      return { symbol: symbol.trim(), prefixed };
    },
    [currency],
  );

  return {
    formatCurrency,
    formatMinorUnitsCurrency,
    getCurrencySymbol,
    getShortCurrencySymbol,
  };
};
